// TIENDA DE PERSONALIZABLES — "Mis pedidos"
//
// El comprador ve el estado de cada solicitud, el motivo si se la han rechazado
// y, cuando está aceptada, elige método de pago, ve los datos de cobro y avisa
// de que ha pagado. Desde que confirmamos el cobro tiene además el chat.
//
// La consulta FILTRA POR uid a la fuerza: las reglas evalúan el `list` documento
// a documento y basta uno ajeno para tumbar la consulta entera.
//
// Criterio visual: design system de la web, y directo. Lo accionable (el panel
// de pago) va arriba del todo y abierto; el resto se despliega si se pide.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { PackOrder, PackPaymentMethod, PacksPaymentInfo } from '@/lib/types';
import { packsShop_getPaymentInfo, packsShop_markOrderSeen, packsShop_reportPayment } from '@/lib/actions';
import { RichText } from '@/components/ui/rich-text';
import { Button } from '@/components/ui/button';
import {
  ESTADOS, EstadoBadge, EstadoTimeline, ImageGrid, OrderChat, BizumLogo, TransferenciaLogo,
  formatEur, formatFechaHora, hexToRgba, toMs,
} from '@/components/tienda/ui';
import {
  ArrowLeft, Loader2, Package, ChevronDown, ChevronUp, Copy, Check, Search,
  CreditCard, MessageSquare, Gem, Info, Clock, AlertCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PersonalizablesPedidosPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'activos' | 'todos'>('activos');
  const [busca, setBusca] = useState('');

  const ordersQuery = useMemo(
    () => (user ? query(collection(db, 'packs_orders'), where('uid', '==', user.uid)) : null),
    [user],
  );
  const { data: raw, isLoading } = useCollection<PackOrder>(ordersQuery);

  // Orden en memoria: junto al filtro por uid, ordenar en la query exigiría índice.
  const orders = useMemo(
    () => (raw || [])
      .map((o) => sanitizeFirestoreData(o) as PackOrder)
      .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt)),
    [raw],
  );

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return orders.filter((o) => {
      const cerrado = ['entregado', 'cancelado', 'rechazado'].includes(o.status);
      if (tab === 'activos' && cerrado) return false;
      if (!q) return true;
      return (o.code || '').toLowerCase().includes(q)
        || (o.productName || '').toLowerCase().includes(q)
        || (o.hermandadNombre || '').toLowerCase().includes(q);
    });
  }, [orders, tab, busca]);

  const porPagar = useMemo(() => orders.filter((o) => o.status === 'aceptado').length, [orders]);

  return (
    <div className="space-y-5 pb-14">
      {/* ── Cabecera ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-primary/15 via-card/45 to-card/20 p-6 md:p-7"
      >
        <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl border border-primary/30 bg-primary/15 text-primary">
              <Package className="h-7 w-7" />
            </div>
            <div>
              <h1 className="font-headline text-3xl font-bold md:text-4xl">Mis pedidos</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">Estado, pago y chat de tus personalizables.</p>
            </div>
          </div>
          <Link to="/jartinet/personalizables">
            <Button variant="outline" className="gap-2 rounded-xl">
              <ArrowLeft className="h-4 w-4" /> Volver a la tienda
            </Button>
          </Link>
        </div>

        {porPagar > 0 && (
          <div className="relative mt-5 flex items-start gap-3 rounded-2xl border border-sky-500/30 bg-sky-500/10 p-4">
            <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
            <p className="text-sm">
              <span className="font-bold text-sky-300">
                {porPagar === 1 ? 'Tienes 1 pedido pendiente de pago.' : `Tienes ${porPagar} pedidos pendientes de pago.`}
              </span>{' '}
              <span className="text-muted-foreground">Ábrelo para ver los datos de Bizum o transferencia.</span>
            </p>
          </div>
        )}
      </motion.div>

      {/* ── Filtros ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex gap-1.5">
          {([['activos', 'En curso'], ['todos', 'Todos']] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-all ${
                tab === k
                  ? 'border-primary/40 bg-primary/15 text-primary'
                  : 'border-white/8 bg-card/40 text-muted-foreground hover:border-white/15 hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por código, producto o hermandad…"
            className="h-10 w-full rounded-xl border border-white/8 bg-card/40 pl-10 pr-4 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/40"
          />
        </div>
      </div>

      {/* ── Lista ── */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : visibles.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-center">
          <Package className="mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="font-bold">
            {tab === 'activos' ? 'No tienes pedidos en curso' : 'Todavía no has pedido ningún personalizable'}
          </p>
          <Link to="/jartinet/personalizables" className="mt-4">
            <Button className="gap-2"><Gem className="h-4 w-4" /> Ir a la tienda</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {visibles.map((o) => <TarjetaPedido key={o.id} order={o} />)}
        </div>
      )}
    </div>
  );
}

// ─── Tarjeta de pedido ──────────────────────────────────────────────────────

function TarjetaPedido({ order }: { order: PackOrder }) {
  const pendientePago = order.status === 'aceptado';
  // Lo que exige acción del usuario se abre solo: es a lo que viene.
  const [abierto, setAbierto] = useState(pendientePago);
  const [vista, setVista] = useState<'estado' | 'chat'>('estado');
  const nuevas = Number(order.unreadUser) || 0;
  const e = ESTADOS[order.status] || ESTADOS.pendiente;
  const Icono = e.icon;

  // Al desplegar, el pedido se da por visto: el contador es por pedido, no por
  // sección, así que solo se limpia abriendo este.
  useEffect(() => {
    if (abierto && nuevas > 0) packsShop_markOrderSeen(order.id, 'user');
  }, [abierto, nuevas, order.id]);

  return (
    <motion.article
      layout
      className="overflow-hidden rounded-2xl border bg-card/40"
      style={{ borderColor: nuevas > 0 || pendientePago ? hexToRgba(e.color, 0.4) : 'rgba(255,255,255,0.08)' }}
    >
      <button
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-white/[0.03]"
      >
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/30">
          {order.productImageUrl
            ? <img src={order.productImageUrl} alt="" className="h-full w-full object-cover" />
            : <div className="grid h-full w-full place-items-center"><Gem className="h-6 w-6 text-primary/40" /></div>}
          {nuevas > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-amber-500 px-1 text-[10px] font-bold text-primary-foreground shadow">
              {nuevas}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-headline text-lg font-bold">{order.productName}</h3>
            <EstadoBadge status={order.status} />
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="font-mono font-bold text-primary">{order.code}</span>
            <span className="truncate">{order.hermandadNombre}</span>
            <span>{formatFechaHora(order.createdAt)}</span>
          </p>
        </div>

        <span className="hidden shrink-0 font-headline text-xl font-black text-primary sm:block">
          {formatEur(order.priceEur)}
        </span>
        <span className="shrink-0 text-muted-foreground">
          {abierto ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </span>
      </button>

      {/* Franja de estado: siempre visible, aunque esté plegado */}
      <div
        className="flex items-center gap-2 border-y px-4 py-2 text-sm"
        style={{ borderColor: hexToRgba(e.color, 0.2), background: hexToRgba(e.color, 0.07) }}
      >
        <Icono className="h-4 w-4 shrink-0" style={{ color: e.color }} />
        <span className="font-semibold" style={{ color: e.color }}>{e.label}.</span>
        <span className="truncate text-muted-foreground">{e.desc}</span>
      </div>

      <AnimatePresence initial={false}>
        {abierto && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-5 p-4 md:p-5">
              {order.chatOpen && (
                <div className="flex gap-1.5">
                  {([['estado', 'Estado del pedido'], ['chat', 'Chat']] as const).map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setVista(k)}
                      className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-all ${
                        vista === k
                          ? 'border-primary/40 bg-primary/15 text-primary'
                          : 'border-white/8 bg-card/40 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {k === 'chat' && <MessageSquare className="h-3.5 w-3.5" />} {label}
                    </button>
                  ))}
                </div>
              )}

              {vista === 'chat' && order.chatOpen ? (
                <OrderChat order={order} />
              ) : (
                <>
                  {/* Lo accionable primero */}
                  {pendientePago && <PanelPago order={order} />}

                  <EstadoTimeline order={order} />

                  {order.status === 'pago_reportado' && (
                    <div className="flex items-start gap-3 rounded-2xl border border-violet-500/25 bg-violet-500/10 p-4">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-400" />
                      <div className="text-sm">
                        <p className="font-bold text-violet-300">Estamos comprobando tu pago</p>
                        <p className="mt-0.5 text-muted-foreground">
                          Nos has dicho que has pagado por{' '}
                          <strong>{order.paymentMethod === 'bizum' ? 'Bizum' : 'transferencia'}</strong>. En cuanto lo
                          veamos reflejado te avisaremos y tu pedido pasará a <em>En proceso</em>.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Detalle */}
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold">Tu encargo</h4>
                      <div className="rounded-2xl border border-white/8 bg-black/20 p-4 text-sm leading-relaxed text-muted-foreground">
                        <RichText>{order.message}</RichText>
                      </div>
                      {order.images && order.images.length > 0 && (
                        <>
                          <h4 className="text-sm font-bold">Fotos de referencia ({order.images.length})</h4>
                          <ImageGrid urls={order.images} />
                        </>
                      )}
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-sm font-bold">Historial</h4>
                      <ol className="space-y-2.5 rounded-2xl border border-white/8 bg-black/20 p-4">
                        {(order.tracking || []).map((t, i) => (
                          <li key={i} className="flex gap-3 text-sm">
                            <span
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                              style={{ background: (ESTADOS as any)[t.status]?.color || '#facc15' }}
                            />
                            <span className="min-w-0">
                              <span className="block font-semibold">{t.note || t.status}</span>
                              <span className="block text-xs text-muted-foreground">{formatFechaHora(t.at)}</span>
                            </span>
                          </li>
                        ))}
                        {(order.tracking || []).length === 0 && (
                          <li className="text-sm text-muted-foreground">Sin eventos.</li>
                        )}
                      </ol>

                      <div className="divide-y divide-white/8 rounded-2xl border border-white/8 bg-black/20 px-4">
                        <Fila k="Hermandad" v={order.hermandadNombre} />
                        <Fila k="Importe" v={formatEur(order.priceEur)} />
                        {order.paymentMethod && (
                          <Fila k="Método de pago" v={order.paymentMethod === 'bizum' ? 'Bizum' : 'Transferencia'} />
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

function Fila({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="truncate font-semibold">{v}</span>
    </div>
  );
}

// ─── Panel de pago ──────────────────────────────────────────────────────────
// Los datos de cobro NO están en el documento del pedido ni en `app_settings`
// (lectura pública): se piden a `packsShop_getPaymentInfo`, que solo los sirve
// al dueño de un pedido ya aceptado.

function PanelPago({ order }: { order: PackOrder }) {
  const { toast } = useToast();
  const [metodo, setMetodo] = useState<PackPaymentMethod | null>(null);
  const [info, setInfo] = useState<{ info: PacksPaymentInfo; concept: string; amount: number } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    packsShop_getPaymentInfo(order.id).then((r) => {
      if (!vivo) return;
      if (r?.success) setInfo({ info: r.info, concept: r.concept, amount: r.amount });
      setCargando(false);
    });
    return () => { vivo = false; };
  }, [order.id]);

  const confirmar = async () => {
    if (!metodo) return;
    if (!window.confirm(
      `¿Confirmas que ya has hecho el pago de ${formatEur(order.priceEur)} por ${metodo === 'bizum' ? 'Bizum' : 'transferencia'}?\n\n` +
      'Lo comprobaremos antes de empezar tu personalizable.',
    )) return;
    setEnviando(true);
    const r = await packsShop_reportPayment(order.id, metodo);
    setEnviando(false);
    if (r?.success) toast({ title: 'Gracias', description: r.message });
    else toast({ title: 'No se pudo comunicar el pago', description: r?.message, variant: 'destructive' });
  };

  const datos = info?.info || {};
  const sinDatos = !cargando && !datos.bizumNumber && !datos.iban;

  return (
    <div className="overflow-hidden rounded-2xl border border-sky-500/30 bg-sky-500/[0.07]">
      <div className="flex flex-wrap items-center gap-3 border-b border-sky-500/20 px-4 py-3">
        <CreditCard className="h-5 w-5 text-sky-400" />
        <h4 className="font-headline text-lg font-bold text-sky-300">Realiza el pago</h4>
        <span className="ml-auto font-headline text-2xl font-black text-primary">{formatEur(order.priceEur)}</span>
      </div>

      <div className="space-y-4 p-4">
        {cargando ? (
          <div className="grid place-items-center py-6"><Loader2 className="h-6 w-6 animate-spin text-sky-400" /></div>
        ) : sinDatos ? (
          <div className="flex items-start gap-3 text-sm">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <p className="text-muted-foreground">
              Todavía no hemos configurado los datos de cobro. Escríbenos por Discord y te los damos.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold">1. Elige cómo quieres pagar</p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <MetodoCard
                seleccionado={metodo === 'bizum'}
                onClick={() => setMetodo('bizum')}
                disabled={!datos.bizumNumber}
                logo={<BizumLogo className="h-9 w-auto" />}
                accent="#0091FF"
              >
                {datos.bizumNumber ? (
                  <>
                    <DatoCopiable label="Número" valor={datos.bizumNumber} />
                    {datos.bizumHolder && <p className="text-xs text-muted-foreground">A nombre de {datos.bizumHolder}</p>}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">No disponible ahora mismo.</p>
                )}
              </MetodoCard>

              <MetodoCard
                seleccionado={metodo === 'transferencia'}
                onClick={() => setMetodo('transferencia')}
                disabled={!datos.iban}
                logo={<TransferenciaLogo className="h-9 w-auto" />}
                accent="#f59e0b"
              >
                {datos.iban ? (
                  <>
                    <DatoCopiable label="IBAN" valor={datos.iban} mono />
                    {datos.ibanHolder && <p className="text-xs text-muted-foreground">Titular: {datos.ibanHolder}</p>}
                    {datos.bankName && <p className="text-xs text-muted-foreground">{datos.bankName}</p>}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">No disponible ahora mismo.</p>
                )}
              </MetodoCard>
            </div>

            {/* El concepto es lo que permite casar el ingreso con el pedido */}
            <div className="rounded-2xl border border-primary/25 bg-primary/8 p-4">
              <p className="text-sm font-semibold">2. Paga poniendo <span className="text-primary">exactamente</span> este concepto</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Es lo que nos permite saber que el dinero es tuyo y de este pedido.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <DatoCopiable label="Concepto" valor={info?.concept || order.code} destacado />
                <DatoCopiable label="Importe exacto" valor={formatEur(info?.amount ?? order.priceEur)} destacado />
              </div>
            </div>

            {datos.notes && (
              <div className="rounded-2xl border border-white/8 bg-black/20 p-4 text-sm leading-relaxed text-muted-foreground">
                <RichText>{datos.notes}</RichText>
              </div>
            )}

            <div>
              <p className="mb-2 text-sm font-semibold">3. Avísanos cuando lo hayas hecho</p>
              <Button
                className="w-full gap-2 rounded-xl py-6 text-base font-bold"
                disabled={!metodo || enviando}
                onClick={confirmar}
              >
                {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
                Ya he hecho el pago
              </Button>
              <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Solo púlsalo cuando hayas pagado de verdad. Lo comprobaremos antes de empezar.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MetodoCard({ seleccionado, onClick, disabled, logo, accent, children }: {
  seleccionado: boolean;
  onClick: () => void;
  disabled?: boolean;
  logo: React.ReactNode;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col gap-3 rounded-2xl border p-4 text-left transition-all disabled:cursor-not-allowed disabled:opacity-45"
      style={{
        borderColor: seleccionado ? hexToRgba(accent, 0.6) : 'rgba(255,255,255,0.08)',
        background: seleccionado ? hexToRgba(accent, 0.1) : 'rgba(0,0,0,0.2)',
        boxShadow: seleccionado ? `0 0 0 3px ${hexToRgba(accent, 0.12)}` : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        {logo}
        <span
          className="grid h-5 w-5 shrink-0 place-items-center rounded-full border-2"
          style={{ borderColor: seleccionado ? accent : 'rgba(255,255,255,0.2)', background: seleccionado ? accent : 'transparent' }}
        >
          {seleccionado && <Check className="h-3 w-3 text-black" />}
        </span>
      </div>
      <div className="space-y-1">{children}</div>
    </button>
  );
}

function DatoCopiable({ label, valor, mono, destacado }: {
  label: string; valor: string; mono?: boolean; destacado?: boolean;
}) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async (ev: React.MouseEvent) => {
    ev.stopPropagation();
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch { /* sin portapapeles: el valor se ve igual */ }
  };
  return (
    <div className={`flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 ${destacado ? 'bg-black/40' : 'bg-black/25'}`}>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70">{label}</p>
        <p className={`truncate ${mono ? 'font-mono' : ''} ${destacado ? 'text-base font-bold text-primary' : 'text-sm font-semibold'}`}>
          {valor}
        </p>
      </div>
      <button
        type="button"
        onClick={copiar}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/10 text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
      >
        {copiado ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}
